import { GateError } from './gate-error';
import { GateState } from './gate-state';

export class DeviceStatus {
    Id: number;
    Configured = 255;
    Online = false;
    FwUpdate: number;
    Errored = false;
    ErrorDescription?: string;
    States: DeviceState[] = [];
    Status?: string;
    CommandId?: number;
    CommandName?: string;
    CommandDescription?: string;
    Percentage?: number;
    Openings?: number;
    Closures?: number;
    InputsStatus?: number[];
    OutputsStatus?: number[];
    AutomationsStatus?: number[];
    Inputs?: Map<number, string>;
    Outputs?: Map<number, string>;
    LastSeen?: Date;
    Limbo? = false;
    ScanConnectedAutomation: boolean;

    constructor(deviceId: number) {
        this.Id = deviceId;
        this.Inputs = new Map();
        this.Outputs = new Map();
    }
}

export interface DeviceStatusUpdate {
    Online?: boolean;
    Configured?: number;
    CommandName?: string;
    CommandDescription?: string;
    Percentage?: number;
    Errored?: boolean;
    ErrorDescription?: string;
    Openings?: number;
    Closures?: number;
    InputsStatus?: number[];
    OutputsStatus?: number[];
    AutomationsStatus?: number[];
    ScanConnectedAutomation?: boolean;
    CrpId?: number;
    Inputs?: Map<number, string>;
    Outputs?: Map<number, string>;
    LastSeen?: Date;
}

export interface DeviceState {
    DeviceEventId: number;
    OperationId: number;
    CommandId: number;
    ErrorCode: number;
    ResponseCode: number;
    Data: number[];
    Payload?: number[];
    DeviceId?: number;
    CrpId?: number;
    StateLabel?: string;
    InputId?: number;
    OutputId?: number;
    State?: string;
    UpdatedAt: Date;
    TaskId?: number;
    Class?: number;
    BoardId?: number,
    LngResVersion?: number,
    GuiResVersion?: number,
    FwVersion?: string,
    MotorBoardRxVersion?: string,
    HwVersion?: number,
    GreenPowerActive?: boolean,
}

export const fillDeviceStatus = (deviceStatus: DeviceStatus, gateStates: GateState[], gateErrors: GateError[]): DeviceStatus => {
    let ds: DeviceStatus = new DeviceStatus(deviceStatus.Id);
    ds = {
        ...ds,
        ...deviceStatus,
    };
    for (const state of deviceStatus.States) {
        newDeviceState(ds, gateStates, gateErrors, state);
    }
    return ds;
};

export const newDeviceState = (ds: DeviceStatus, gateStates: GateState[], gateErrors: GateError[], state: DeviceState): void => {
    if (errored(state)) {
        const errCode = state.Data[0];
        if (errCode === 0) {
            ds.Errored = false;
            ds.ErrorDescription = '';
            return;
        }
        ds.Errored = true;
        const errorDetails = gateErrors.find(ec => ec.Id === errCode);
        if (errorDetails) {
            ds.ErrorDescription = errorDetails.Description;
        }
        return;
    }
    if (commandOne(state)) {
        if (gateStates.length === 0) {
            return;
        }
        ds.CommandId = state.Data[0];
        const stateDetails = gateStates.find(s => s.Id === ds.CommandId);
        if (!stateDetails) {
            console.error(`could not find command ${ds.CommandId} in possible states`);
            return;
        }
        ds.CommandName = stateDetails.Name;
        ds.CommandDescription = stateDetails.Description;
        ds.Percentage = state.Data[1];
        return;
    }
    if (maneuvers(state)) {
        const u8 = new Uint8Array(state.Data.slice(0, 8));
        ds.Openings = new DataView(u8.buffer).getInt32(0);
        ds.Closures = new DataView(u8.buffer).getInt32(4);
        return;
    }
    if (gatewayIOinput(state)) {
        ds.InputsStatus = state.Data;
        return;
    }
    if (scanConnectedAutomations(state)) {
        if ((state.Data[0] & 2) === 2) {
            ds.ScanConnectedAutomation = true;
        } else {
            ds.ScanConnectedAutomation = false;
        }
        //ds.ScanConnectedAutomation = true;
    }
    if (gatewayIOoutput(state)) {
        ds.OutputsStatus = state.Data;
        return;
    }
    if (gatewayAutomationsStatus(state)) {
        ds.AutomationsStatus = state.Data;
        return;
    }
    if (multiIOinput(state)) {
        ds.Inputs.set(state.InputId, state.State);
        return;
    }
    if (multiIOoutput(state)) {
        ds.Outputs.set(state.OutputId, state.State);
        return;
    }
};

export const errored = (state: DeviceState): boolean => state.OperationId === 3 &&
    state.CommandId === 3 &&
    state.Data[0] !== 0;

const commandOne = (state: DeviceState): boolean => state.OperationId === 3 && state.CommandId === 1;

const maneuvers = (state: DeviceState): boolean => state.OperationId === 3 &&
    state.CommandId === 18 &&
    [0, 10].includes(state.ErrorCode) &&
    [0, 10].includes(state.ResponseCode);

const gatewayIOinput = (state: DeviceState): boolean => state.OperationId === 10 && state.ErrorCode === 0 && state.ResponseCode === 0;

const gatewayIOoutput = (state: DeviceState): boolean => state.OperationId === 11 && state.ErrorCode === 0 && state.ResponseCode === 0;

const gatewayAutomationsStatus = (state: DeviceState): boolean => state.OperationId === 3 && state.CommandId === 22
    && state.ErrorCode === 0 && state.ResponseCode === 0;

const multiIOinput = (state: DeviceState): boolean => state.InputId !== undefined;

const multiIOoutput = (state: DeviceState): boolean => state.OutputId !== undefined;

const scanConnectedAutomations = (state: DeviceState): boolean => state.OperationId === 3 && state.CommandId === 22
    && state.ErrorCode === 0 && state.ResponseCode === 0;